import { ThemeProvider } from "@mui/material";
import { theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import {
  AuthorDetail,
  Comment,
  Content,
  DesktopHeaderAdSlot,
  Leading,
  Left,
  MobileContentFooterAdSlot,
  MobileContentHeaderAdSlot,
  MobileFooterAdSlot,
  MoreOnThis,
  TabletContentFooterAdSlot,
  Topic,
  TopPicksAdSlot,
} from "~/components/article/article-render/generic-article/styles";
import { useReduceWhitespaceByAvailableSpace } from "~/components/article/article-render/hooks";
import { useContentHelper } from "~/components/content/hooks";
import type { opinionArticleContent$key } from "~/queries/__generated__/opinionArticleContent.graphql";

import {
  OpinionArticleContainer,
  StyledArticleContentRightSection,
  StyledOpinionHeader,
  WidgetsBottom,
} from "./styles";
import { theme as OpinionArticleTheme } from "./theme";

type Props = {
  reference: opinionArticleContent$key;
} & ArticleRenderChildProps;

export const OpinionArticle: FunctionComponent<Props> = ({
  isShowInlinePaywall,
  provider,
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment opinionArticleContent on Content {
        ...headerOpinionArticle
        ...hooksContentHelperContent
        ...contentSchemaRenderContent
      }
    `,
    reference_,
  );

  const { isAuthorSCMPColumnist, isEditorialOpinionType } = useContentHelper(content);
  const isColumnistArticle = (!isEditorialOpinionType && isAuthorSCMPColumnist) ?? false;
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace(
    {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    isShowInlinePaywall,
  );

  const renderLeading = () =>
    isColumnistArticle ? null : (
      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "hidden",
            tabletUp: "show",
          },
          responsiveImageVariants: {
            desktopUp: "normal",
            mobileUp: "square",
            tabletUp: "normal",
          },
        })}
      </Leading>
    );

  return (
    <ThemeProvider theme={OpinionArticleTheme}>
      <OpinionArticleContainer $isColumnistArticle={isColumnistArticle}>
        <WidgetsBottom>
          {provider.leftHandSideWidgets({
            topPicksWidgetProps: {
              maximumHeight: reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.maximumHeight,
              slots: {
                footer: {
                  estimatedHeight: 260,
                  node: (
                    <TopPicksAdSlot>
                      {provider.adslot({
                        adUnit: "d_native3b",
                        breakpoint: theme.breakpoints.up("desktop"),
                        sizes: ["fluid"],
                      })}
                    </TopPicksAdSlot>
                  ),
                },
                header: {
                  estimatedHeight: 260,
                  node: (
                    <TopPicksAdSlot>
                      {provider.adslot({
                        adUnit: "d_native2b",
                        breakpoint: theme.breakpoints.up("desktop"),
                        fluidDefaultSize: {
                          height: 260,
                        },
                        sizes: ["fluid"],
                      })}
                    </TopPicksAdSlot>
                  ),
                },
              },
            },
          })}
        </WidgetsBottom>
        {reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.trackerNode}
        <StyledOpinionHeader
          isColumnistArticle={isColumnistArticle}
          isEditorialOpinionType={isEditorialOpinionType}
          provider={provider}
          reference={content}
        />
        {renderLeading()}
        <Content>
          {provider.body({
            inlineAdSlots: {
              "0": {
                adUnit: "outstream1",
              },
              "1": {
                adUnit: "m_lrec1",
              },
              "2": {
                adUnit: "d_lrec2",
              },
              "3": {
                adUnit: "m_lrec4d",
              },
              "4": {
                adUnit: "m_lrec4e",
              },
              "5": {
                adUnit: "m_lrec4f",
              },
              "6": {
                adUnit: "native5",
              },
            },
          })}
        </Content>
        <Comment>{provider.commentTrigger()}</Comment>
        <MoreOnThis>{provider.moreOnThis({ variant: "horizontal" })}</MoreOnThis>
        <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
        <Topic>{provider.topics()}</Topic>
        <Left $stickyStackedHeight={appBarStickyHeight}>
          {provider.shareWidget()}
          {provider.actionWidget()}
        </Left>
        <StyledArticleContentRightSection
          {...reduceWhitespaceByAvailableSpaceSpec.right}
          provider={provider}
        />
        {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}
        <DesktopHeaderAdSlot>
          {provider.adslot({
            adUnit: "d_banner1",
            breakpoint: theme.breakpoints.up("desktop"),
            sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
          })}
        </DesktopHeaderAdSlot>
        <TabletContentFooterAdSlot>
          {provider.adslot({
            adUnit: "native4",
            autoRefreshOptions: {
              disableIntervalRefresh: true,
              disableScrollBackRefresh: true,
            },
            breakpoint: theme.breakpoints.only("tablet"),
            sizes: ["fluid"],
          })}
        </TabletContentFooterAdSlot>
        <MobileContentHeaderAdSlot>
          {provider.adslot({
            adUnit: "m_banner1",
            breakpoint: theme.breakpoints.down("tablet"),
            sizes: [[300, 250], "fluid"],
            withBackground: true,
            withLabel: true,
          })}
        </MobileContentHeaderAdSlot>
        <MobileContentFooterAdSlot>
          {provider.adslot({
            adUnit: "native4",
            autoRefreshOptions: {
              disableIntervalRefresh: true,
              disableScrollBackRefresh: true,
            },
            breakpoint: theme.breakpoints.down("tablet"),
            sizes: ["fluid"],
          })}
        </MobileContentFooterAdSlot>
        <MobileFooterAdSlot>
          {provider.adslot({
            adUnit: "m_lrec3a",
            breakpoint: theme.breakpoints.down("tablet"),
            sizes: [[300, 250], "fluid"],
            withBackground: true,
            withLabel: true,
          })}
        </MobileFooterAdSlot>
      </OpinionArticleContainer>
    </ThemeProvider>
  );
};

OpinionArticle.displayName = "OpinionArticle";
